/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-restricted-types */
import { useCallback, useEffect, useRef, useState } from 'react';
import { sessionManager } from './security-utils';
import { useAuth } from './use-auth';

export interface UseGlobalActivityTrackerOptions {
  /**
   * Enable debug logging
   */
  enableDebugLogs?: boolean;

  /**
   * Activities to track (default: mouse, keyboard, scroll, touch)
   */
  activities?: string[];

  /**
   * Throttle interval for activity updates in milliseconds (default: 30000 = 30 seconds)
   */
  throttleInterval?: number;

  /**
   * How often to check session validity in milliseconds (default: 60000 = 1 minute)
   */
  sessionCheckInterval?: number;

  /**
   * Callback when session expires
   */
  onSessionExpired?: (sessionId: string) => void;

  /**
   * Callback when activity is detected
   */
  onActivity?: (sessionId: string) => void;

  /**
   * Warning time before session expires in minutes (default: 5 minutes)
   */
  sessionWarningMinutes?: number;

  /**
   * Callback when session is about to expire
   */
  onSessionWarning?: (minutesLeft: number) => void;
}

export interface ActivityTrackerState {
  sessionId: string;
  isSessionValid: boolean;
  lastActivity: Date;
  lastActivityTimestamp: number; // Force React to detect changes
  minutesUntilExpiry: number;
  isWarningActive: boolean;
}

/**
 * Global activity tracker hook that monitors user activity and manages session lifecycle
 * This hook should be used once at the app level to track activity globally
 */
export const useGlobalActivityTracker = (
  options: UseGlobalActivityTrackerOptions = {},
): ActivityTrackerState => {
  const {
    enableDebugLogs = false,
    activities = ['keypress', 'scroll', 'click'], // ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'],
    throttleInterval = 30000, // 30 seconds
    sessionCheckInterval = 60000, // 1 minute
    onSessionExpired,
    onActivity,
    sessionWarningMinutes = 5,
    onSessionWarning,
  } = options;

  const auth = useAuth();
  const [sessionId] = useState(() => crypto.randomUUID());
  const [state, setState] = useState<ActivityTrackerState>({
    sessionId,
    isSessionValid: true,
    lastActivity: new Date(),
    lastActivityTimestamp: Date.now(),
    minutesUntilExpiry: 60, // Default 60 minutes
    isWarningActive: false,
  });

  const throttleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const sessionCheckTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningShownRef = useRef<boolean>(false);

  // Store callbacks in refs to avoid recreating effect when they change
  const onActivityRef = useRef(onActivity);
  const onSessionExpiredRef = useRef(onSessionExpired);
  const onSessionWarningRef = useRef(onSessionWarning);

  // Update refs when callbacks change
  useEffect(() => {
    onActivityRef.current = onActivity;
    onSessionExpiredRef.current = onSessionExpired;
    onSessionWarningRef.current = onSessionWarning;
  });

  // Initialize session
  useEffect(() => {
    if (auth.isAuthenticated) {
      sessionManager.createSession(sessionId);
      if (enableDebugLogs) {
        console.log('🟢 Global Activity Tracker: Session created', {
          sessionId,
        });
      }
    }
  }, [sessionId, auth.isAuthenticated, enableDebugLogs]);

  // Handle activity update
  const handleActivity = useCallback(() => {
    if (enableDebugLogs) {
      console.log('🏃 handleActivity called - auth status:', auth.isAuthenticated);
    }

    if (!auth.isAuthenticated) {
      if (enableDebugLogs) {
        console.log('❌ User not authenticated, skipping activity update');
      }

      return;
    }

    if (enableDebugLogs) {
      console.log('📝 Updating session activity and state');
    }

    sessionManager.updateActivity(sessionId);
    const newActivityTime = new Date();
    const newTimestamp = Date.now();

    setState(prev => {
      if (enableDebugLogs) {
        console.log('🔄 BEFORE setState - prev timestamp:', prev.lastActivityTimestamp);
        console.log(
          '🔄 SETTING new lastActivity:',
          newActivityTime.toLocaleTimeString(),
          'timestamp:',
          newTimestamp,
        );
      }

      const newState = {
        ...prev,
        lastActivity: newActivityTime,
        lastActivityTimestamp: newTimestamp,
        isWarningActive: false,
      };

      if (enableDebugLogs) {
        console.log('🔄 AFTER setState - new timestamp:', newState.lastActivityTimestamp);
        console.log(
          '🔄 State change detected:',
          prev.lastActivityTimestamp !== newState.lastActivityTimestamp,
        );
      }

      return newState;
    });

    // Reset warning flag when user is active
    warningShownRef.current = false;

    if (enableDebugLogs) {
      console.log('✅ Activity processed successfully', { sessionId });
    }

    if (onActivityRef.current) {
      onActivityRef.current(sessionId);
    }
  }, [sessionId, auth.isAuthenticated, enableDebugLogs]);

  // Store handleActivity in a ref to avoid recreating throttledHandleActivity
  const handleActivityRef = useRef(handleActivity);

  useEffect(() => {
    if (enableDebugLogs) {
      console.log('🔄 Updating handleActivityRef with new handleActivity function');
    }

    handleActivityRef.current = handleActivity;
  }, [handleActivity, enableDebugLogs]);

  // Throttled activity handler - now stable
  const throttledHandleActivity = useCallback(() => {
    if (throttleTimerRef.current) {
      if (enableDebugLogs) {
        console.log('⏳ Activity throttled - timer active');
      }

      return;
    }

    if (enableDebugLogs) {
      console.log('🔄 Starting throttle timer for', throttleInterval, 'ms');
    }

    throttleTimerRef.current = setTimeout(() => {
      if (enableDebugLogs) {
        console.log('⚡ Throttle timer fired - calling handleActivity');
      }

      handleActivityRef.current();
      throttleTimerRef.current = null;
    }, throttleInterval);
  }, [throttleInterval, enableDebugLogs]);

  // Check session validity and calculate time until expiry
  const checkSessionValidity = useCallback(() => {
    if (!auth.isAuthenticated) {
      setState(prev => ({ ...prev, isSessionValid: false }));
      return;
    }

    const isValid = sessionManager.isSessionValid(sessionId);

    // Calculate minutes until expiry and handle warnings in one setState callback
    const sessionTimeoutMs = 60 * 60 * 1000; // 60 minutes default (you can make this configurable)
    const now = Date.now();

    setState(prev => {
      const sessionStart = prev.lastActivity.getTime();
      const expiryTime = sessionStart + sessionTimeoutMs;
      const minutesLeft = Math.max(0, Math.floor((expiryTime - now) / (60 * 1000)));

      // Show warning if session is about to expire (do this inside setState to avoid dependency issues)
      if (minutesLeft <= sessionWarningMinutes && minutesLeft > 0 && !warningShownRef.current) {
        warningShownRef.current = true;
        if (onSessionWarningRef.current) {
          onSessionWarningRef.current(minutesLeft);
        }

        if (enableDebugLogs) {
          console.warn(`⚠️ Session expires in ${minutesLeft} minutes`);
        }
      }

      return {
        ...prev,
        isSessionValid: isValid,
        minutesUntilExpiry: minutesLeft,
        isWarningActive: minutesLeft <= sessionWarningMinutes && minutesLeft > 0,
      };
    });

    // Handle session expiration
    if (!isValid && auth.isAuthenticated) {
      if (enableDebugLogs) {
        console.error('🔴 Session expired, logging out user', { sessionId });
      }

      if (onSessionExpiredRef.current) {
        onSessionExpiredRef.current(sessionId);
      }

      // Clean up session
      sessionManager.destroySession(sessionId);

      // Logout user
      auth.logout();
    }
  }, [sessionId, auth, sessionWarningMinutes, enableDebugLogs]);

  // Set up global activity listeners
  useEffect(() => {
    if (!auth.isAuthenticated) {
      return;
    }

    const handleGlobalActivity = (event: Event) => {
      // Debug: Log every activity detection
      if (enableDebugLogs) {
        console.log('🎯 RAW activity detected:', event.type, 'at', new Date().toLocaleTimeString());
      }

      // Ignore activity on login/auth pages
      if (
        window.location.pathname.includes('/login') ||
        window.location.pathname.includes('/auth')
      ) {
        if (enableDebugLogs) {
          console.log('❌ Activity ignored - on login/auth page');
        }

        return;
      }

      if (enableDebugLogs) {
        console.log('✅ Activity accepted, calling throttledHandleActivity');
      }

      // TEMPORARY: Call handleActivity directly to bypass throttling
      if (enableDebugLogs) {
        console.log('🚨 BYPASS: Calling handleActivity directly for testing');
        handleActivityRef.current();
      } else {
        throttledHandleActivity();
      }
    };

    // Add event listeners for all specified activities
    activities.forEach(activity => {
      document.addEventListener(activity, handleGlobalActivity, {
        passive: true,
      });
    });

    if (enableDebugLogs) {
      console.log('🎯 Global activity listeners attached', {
        activities,
        sessionId,
      });
    }

    // Cleanup function
    return () => {
      activities.forEach(activity => {
        document.removeEventListener(activity, handleGlobalActivity);
      });

      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current);
        throttleTimerRef.current = null;
      }

      if (enableDebugLogs) {
        console.log('🧹 Global activity listeners removed', { sessionId });
      }
    };
  }, [
    throttledHandleActivity,
    auth.isAuthenticated,
    enableDebugLogs,
    sessionId,
    // Note: activities array is usually static, but keeping it as dependency for safety
    activities,
  ]);

  // Set up session validity checker
  useEffect(() => {
    if (!auth.isAuthenticated) {
      return;
    }

    const startSessionCheck = () => {
      checkSessionValidity(); // Check immediately
      sessionCheckTimerRef.current = setInterval(checkSessionValidity, sessionCheckInterval);
    };

    startSessionCheck();

    return () => {
      if (sessionCheckTimerRef.current) {
        clearInterval(sessionCheckTimerRef.current);
        sessionCheckTimerRef.current = null;
      }
    };
  }, [checkSessionValidity, sessionCheckInterval, auth.isAuthenticated]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current);
      }

      if (sessionCheckTimerRef.current) {
        clearInterval(sessionCheckTimerRef.current);
      }
    },
    [],
  );

  return state;
};

/**
 * Simple component to display session status (useful for development)
 */
export interface GlobalSessionStatusProps {
  className?: string;
  showDetails?: boolean;
}

export const GlobalSessionStatus: React.FC<
  GlobalSessionStatusProps & {
    sessionState?: ActivityTrackerState;
  }
> = ({
  className = 'fixed bottom-4 right-4 bg-white border border-gray-300 rounded-lg p-3 shadow-lg text-xs',
  showDetails = false,
  sessionState,
}) => {
  // If no sessionState provided, create our own (fallback for standalone usage)
  const fallbackSessionState = useGlobalActivityTracker({
    enableDebugLogs: true,
  });
  const activeSessionState = sessionState || fallbackSessionState;

  if (!activeSessionState.isSessionValid) {
    return null;
  }

  return (
    <div className={className}>
      <div className='font-semibold mb-1'>
        Session:{' '}
        {activeSessionState.isSessionValid ? (
          <>
            <span role='img' aria-label='active'>
              🟢
            </span>{' '}
            Active
          </>
        ) : (
          <>
            <span role='img' aria-label='expired'>
              🔴
            </span>{' '}
            Expired
          </>
        )}
      </div>
      {showDetails && (
        <>
          <div>ID: {activeSessionState.sessionId.substring(0, 8)}...</div>
          <div>Expires in: {activeSessionState.minutesUntilExpiry}m</div>
          <div>Last activity: {activeSessionState.lastActivity.toLocaleTimeString()}</div>
          {activeSessionState.isWarningActive && (
            <div className='text-orange-600 font-bold'>
              <span role='img' aria-label='warning'>
                ⚠️
              </span>{' '}
              Session expiring soon!
            </div>
          )}
        </>
      )}
    </div>
  );
};
